import { useState, useEffect, forwardRef, useImperativeHandle, useRef } from "react";
import { Loading } from "@gpstrackit/react-controls";
import { useSelector, useDispatch } from "react-redux";

import {
    getPreferences,
    savePreference,
    clearRedux,
    updatePreferenceView,
    updatePreferencesOther,
    clearReturnedKey
} from 'Redux/actions';

const PreferencesContainer = forwardRef((props, ref) => {
    
    const user = useSelector(state => state.securityRedux.user);
    const dispatch = useDispatch()
    const { force = false } = props;

    const { preferencesOther, returnedKeys } = useSelector(state => state.preferencesRedux);
    const [loadPage, setLoadPage] = useState(false);
    
    // Fix B: Prevent re-initialization - track if this instance already initialized
    const hasInitialized = useRef(false);

    useEffect(() => {
        dispatch(getPreferences("Other", user?.userId, force))
    }, [])

    // Fix A + B Combined: Use scoped returnedKeys AND hasInitialized guard
    const isKeyReturned = returnedKeys?.[props.preferencesKey];
    
    // Helper to apply preferences - shared between fresh fetch and remount scenarios
    const applyPreferences = () => {
        if (hasInitialized.current) return; // Already initialized
        
        const preferencesKeyTemp = preferencesOther?.find(x => x.preferenceKey === props.preferencesKey);
        if (!preferencesKeyTemp) {
            // FIX: No preferences saved for this key — still notify the component
            // so it can perform initial data fetch with default settings.
            // Without this, views that push mandatory conditions (e.g., companyId) inside
            // onChangeConditions would never get their initial getData() triggered,
            // causing: (1) empty table on load, (2) refresh without mandatory filters.
            // See: BUG-ANALYSIS-no-preferences-mandatory-conditions.md
            hasInitialized.current = true;
            setLoadPage(true);
            if (props.onInitialPreferences) {
                props.onInitialPreferences({}); // Empty preferences = use defaults
            }
            return;
        }
        
        hasInitialized.current = true;
        setLoadPage(true);
        
        let preferencesOtherJSON = preferencesKeyTemp.preferenceValue !== "" 
            ? preferencesKeyTemp.preferenceValue 
            : "{}";
        preferencesOtherJSON = JSON.parse(preferencesOtherJSON);
        
        if (props.onInitialPreferences) {
            props.onInitialPreferences(preferencesOtherJSON);
        }
    };
    
    // Original flow: React to fresh preferences fetch (returnedKeys signal)
    useEffect(() => {
        if (isKeyReturned && !hasInitialized.current) {
            // Clear only OUR key, not affect other components
            dispatch(clearReturnedKey(props.preferencesKey));
            applyPreferences();
        }
    }, [isKeyReturned]);
    
    // Fix C: Handle remount when preferences already exist in Redux
    // On remount, returnedKeys[key] is cleared but preferencesOther still has the data
    // This ensures onInitialPreferences is called even when component remounts
    useEffect(() => {
        if (!hasInitialized.current && preferencesOther?.length > 0) {
            applyPreferences();
        }
    }, [preferencesOther])

    useImperativeHandle(ref, () => ({
        getPreferences: (entity, key) => {
            let preferencesKeyTemp
            let preferencesJSON
            preferencesKeyTemp = preferencesOther && preferencesOther.find(x => x.preferenceKey === key)
            preferencesJSON = preferencesKeyTemp && preferencesKeyTemp.preferenceValue != "" && preferencesKeyTemp.preferenceValue || "{}"
            preferencesJSON = JSON.parse(preferencesJSON)
            return preferencesJSON
        },
        savePreference: (entity, key, preference) => {
            dispatch(updatePreferenceView(true))
            dispatch(savePreference("Other", user?.userId, key, preference))

            // Optimistic update
            let newPreferencesOther = preferencesOther ? [...preferencesOther] : [];
            let index = newPreferencesOther.findIndex(x => x.preferenceKey === key);
            let newItem = {
                entityName: entity,
                entityId: user?.userId,
                preferenceKey: key,
                preferenceValue: JSON.stringify(preference)
            };

            if (index !== -1) {
                newPreferencesOther[index] = { ...newPreferencesOther[index], ...newItem };
            } else {
                newPreferencesOther.push(newItem);
            }

            dispatch(updatePreferencesOther(newPreferencesOther));

            // Update localStorage
            try {
                let data = localStorage.getItem("preferencesOther");
                let storageData = data ? JSON.parse(data) : { date: new Date(), data: [] };
                storageData.data = newPreferencesOther;
                localStorage.setItem("preferencesOther", JSON.stringify(storageData));
            } catch (error) {
                console.error("Failed to update preferencesOther in localStorage", error);
            }
        }
    }));

    return (
        <>
            {/* !loadPage ? <Loading overlay show={!loadPage} /> : */ props.children}
        </>
    )
})

export default PreferencesContainer;